import React, { useEffect, useState } from 'react';
import classNames from 'classnames';

// Import configs and components
import { useConfiguration } from '../../../../context/configurationContext';
import { lazyLoadWithDimensions } from '../../../../util/uiHelpers';
import { FormattedMessage } from '../../../../util/reactIntl';

import {
  ListingCard,
  IconSpinner,
  ErrorMessage,
  NamedLink,
  CARD_IMAGE_ASPECT_RATIO,
  CARD_IMAGE_ASPECT_RATIO_SMALL_HEIGHT_LAPTOP,
} from '../../../../components';

import Field, { hasDataInFields } from '../../Field';
import SectionContainer from '../SectionContainer';
import FilterPills from './FilterPills';

import css from './SectionListings.module.css';

const KEY_ARROW_LEFT = 'ArrowLeft';
const KEY_ARROW_RIGHT = 'ArrowRight';
const MAX_MOBILE_SCREEN_WIDTH = 768;

// Configuration for supported column layouts
// Only 3 and 4 columns are supported in this component
const COLUMN_CONFIG = {
  3: {
    css: css.threeColumns,
    responsiveImageSizes: '(max-width: 767px) 100vw, (max-width: 1024px) 33vw, 330px',
  },
  4: {
    css: css.fourColumns,
    responsiveImageSizes: '(max-width: 767px) 100vw, (max-width: 1024px) 33vw, 240px',
  },
};

/**
 * Get the CSS class for the specified number of columns
 * @param {number} numColumns - Number of columns (3 or 4)
 * @returns {string} CSS class for the column layout, defaults to 3 columns
 */
const getColumnCSS = numColumns => {
  const config = COLUMN_CONFIG[numColumns];
  return config ? config.css : COLUMN_CONFIG[3].css;
};

const getResponsiveImageSizes = numColumns => {
  const config = COLUMN_CONFIG[numColumns];
  return config ? config.responsiveImageSizes : COLUMN_CONFIG[3].responsiveImageSizes;
};

const parseAspectRatio = aspectRatio => {
  const [width, height] = aspectRatio.split('/').map(Number);
  return width / height;
};

const isMobileViewport = () => {
  const hasMatchMedia = typeof window !== 'undefined' && window?.matchMedia;
  return hasMatchMedia
    ? window.matchMedia(`(max-width: ${MAX_MOBILE_SCREEN_WIDTH}px)`)?.matches
    : false;
};

// Keep in step with `--smallHeightLaptop` in customMediaQueries.css and
// CARD_IMAGE_ASPECT_RATIO_SMALL_HEIGHT_LAPTOP on ListingCard.
const SMALL_HEIGHT_LAPTOP_MQ =
  '(max-height: 768px) and (min-height: 400px) and (min-width: 1024px) and (max-width: 1480px)';

const isSmallHeightLaptopViewport = () => {
  const hasMatchMedia = typeof window !== 'undefined' && window?.matchMedia;
  return hasMatchMedia ? window.matchMedia(SMALL_HEIGHT_LAPTOP_MQ)?.matches : false;
};

/**
 * Calculate the dynamic height for the carousel container based on card dimensions
 *
 * ÔÜá´©Å This function contains hardcoded values that refer to the properties defined in ListingCard.module.css
 * If you modify ListingCard's font sizes, padding, margins, or layout you need to also update this function to match.
 * See ListingCard.module.css for the properties this function refers to (search ÔÜá´©Å in ListingCard.module.css to find the relevant properties).
 *
 * @param {number} numColumns - Number of columns in the layout
 * @param {Object} config - Configuration object containing layout settings
 * @param {number} carouselWidth - Width of the carousel container
 * @param {boolean} isMobileBreakpoint - Whether the viewport is mobile
 * @returns {number} Calculated height in pixels
 */
const calculateCarouselHeight = (
  numColumns,
  config,
  carouselWidth,
  isMobileBreakpoint = false,
  error,
  noListingsFound
) => {
  const errorMessageHeight = 250;
  const noListingsFoundHeight = 220;

  if (error) {
    return errorMessageHeight;
  }

  if (noListingsFound) {
    return noListingsFoundHeight;
  }

  // Before the ResizeObserver fires, carouselWidth is null ÔÇö return a safe
  // placeholder so the container is never 0px tall, which would prevent the
  // lazyLoadWithDimensions HOC from ever rendering (it only renders when both
  // clientWidth and clientHeight are non-zero).
  if (!carouselWidth) {
    return 500;
  }

  // Not config.layout.listingImage.aspectRatio: ListingCard sets its own
  // portrait crop, and the hosted value is landscape. Reserving space from the
  // hosted ratio left every card clipped by about 120px.
  const thumbnailAspectRatio = isSmallHeightLaptopViewport()
    ? CARD_IMAGE_ASPECT_RATIO_SMALL_HEIGHT_LAPTOP
    : CARD_IMAGE_ASPECT_RATIO;
  const paddingHorizontal = 2 * 32; // 2x32px
  // These mirror ListingCard.module.css exactly ÔÇö keep them in step with it.
  // .title: 12px * 1.4 line-height, + 4px margin-bottom
  const titleHeightSingleLine = 17 + 4;
  const titleHeightDoubleLine = titleHeightSingleLine * 2;
  // .info: padding 12px top + 20px bottom
  const cardInfoPadding = 12 + 20;
  // .secondaryLine: 13px * 1.4 (min-height 18px), + 7px margin-bottom
  const secondaryLineHeight = 18 + 7;
  // .price: 13px * 1.4, no margins
  const priceHeight = 18;
  // .buyPrice: 13px * 1.4 + 2px margin-top. Only rendered for listings that can
  // be both rented and bought, but reserved for every card ÔÇö the carousel has a
  // fixed height, so an unreserved row would clip those cards instead.
  const buyPriceHeight = 18 + 2;
  const authorInfoHeight = 24;
  const contentMaxWidthPages = 1120;
  const containerPaddingTop = 32;
  const containerPaddingBottom = 24;

  // Mobile (<=768px): .title 11px, .secondaryLine/.price 12px, .info padding-top 9px
  const priceHeightMobile = 17;
  const authorInfoHeightMobile = 18 + 4 + 4; // 18 + padding top + padding bottom
  const titleHeightSingleLineMobile = 15 + 4;
  const secondaryLineHeightMobile = 18 + 7;
  const cardInfoPaddingMobile = 9 + 20;
  const cardInfoHeightMobile =
    priceHeightMobile +
    buyPriceHeight +
    authorInfoHeightMobile +
    titleHeightSingleLineMobile +
    secondaryLineHeightMobile +
    cardInfoPaddingMobile;

  const parsedAspectRatio = parseAspectRatio(thumbnailAspectRatio);

  const gutters = isMobileBreakpoint ? 0 : numColumns === 3 ? 64 : 96;

  const mainColumnWidth = Math.min(contentMaxWidthPages, carouselWidth);
  const cardWidth =
    (mainColumnWidth - paddingHorizontal - gutters) / (isMobileBreakpoint ? 1 : numColumns);
  const cardImageHeight = cardWidth / parsedAspectRatio;
  const cardInfoHeight =
    priceHeight +
    buyPriceHeight +
    titleHeightSingleLine +
    secondaryLineHeight +
    authorInfoHeight +
    cardInfoPadding;

  const totalCardHeight =
    cardImageHeight + (isMobileBreakpoint ? cardInfoHeightMobile : cardInfoHeight);
  const totalWithPaddings = totalCardHeight + containerPaddingTop + containerPaddingBottom;

  return Math.ceil(totalWithPaddings);
};

/**
 * Component that renders the listing cards in a carousel layout
 * Used with lazy loading wrapper for performance optimization
 * @param {Object} props - Component properties
 * @param {number} props.numColumns - Number of columns to display
 * @param {Array} props.listings - Array of listing data
 * @param {React.RefObject} props.sliderRef - Ref object for the slider element
 * @param {boolean} props.darkMode - Whether to apply dark mode styling
 * @returns {JSX.Element} Carousel container with listing cards
 */
const ListingCarouselComponent = props => {
  const {
    numColumns,
    listings,
    sliderRef,
    darkMode,
    onFetchFeaturedListings,
    fetched,
    inProgress,
    parentPage,
    sectionId,
    config,
    error,
    allSections,
    isInsideContainer,
  } = props;

  const listingImageConfig = config.layout.listingImage;

  useEffect(() => {
    if (!fetched && inProgress !== true && !error) {
      onFetchFeaturedListings(sectionId, parentPage, listingImageConfig, allSections);
    }
  }, []);

  if (inProgress == true) {
    return <IconSpinner className={css.centeredContent} />;
  }

  if (error) {
    return (
      <div className={css.centeredMessageContainer} role="alert">
        <h4 className={css.genericErrorTitle}>
          <FormattedMessage id="SectionListings.genericErrorTitle" />
        </h4>
        <ErrorMessage error={error} />
      </div>
    );
  }

  if (listings.length === 0) {
    return (
      <div className={css.centeredMessageContainer} role="status">
        <p className={css.noListingsFound}>
          <FormattedMessage id="SectionListings.noListingsFoundInfo" />
        </p>
        <NamedLink name="SearchPage" className={css.ctaButton}>
          <FormattedMessage id="SectionListings.noListingsFoundCTA" />
        </NamedLink>
      </div>
    );
  }

  return (
    <ul
      className={classNames(getColumnCSS(numColumns, false), {
        [css.centeredRow]: listings.length < numColumns,
      })}
      ref={sliderRef}
      role="list"
    >
      {listings.map(listing => (
        <li key={listing.id.uuid} className={css.listItem}>
          <ListingCard
            className={classNames(css.card, { [css.isInsideContainer]: isInsideContainer })}
            aspectRatioClassName={css.carouselImageHoverEffect}
            listing={listing}
            darkMode={darkMode}
            renderSizes={getResponsiveImageSizes(numColumns)}
            lazyLoadImage={!isInsideContainer}
          />
        </li>
      ))}
    </ul>
  );
};

const LazyListingCarouselComponent = lazyLoadWithDimensions(ListingCarouselComponent);

/**
 * Main component for rendering a listings section with carousel functionality
 * Supports 3 or 4 column layouts with horizontal scrolling and responsive behavior
 * @param {Object} props - Component properties
 * @param {string} props.sectionId - Unique identifier for this section
 * @param {number} props.numColumns - Number of columns (3 or 4, defaults to 3)
 * @param {Object} props.appearance - Styling configuration including text color
 * @param {Object} props.title - Title field data
 * @param {Object} props.description - Description field data
 * @param {Object} props.callToAction - CTA button field data
 * @returns {JSX.Element} Complete listings section with header and carousel
 */
const SectionListings = props => {
  const config = useConfiguration();
  const {
    sectionId,
    className,
    rootClassName,
    defaultClasses,
    numColumns = 3,
    appearance,
    title,
    description,
    callToAction,
    options,
    allSections,
    isInsideContainer,
    listingSearchQuery,
  } = props;

  const { featuredListings } = options;
  const {
    onFetchFeaturedListings,
    getListingEntitiesById,
    parentPage,
    featuredListingData,
  } = featuredListings;

  const listingIds = featuredListingData?.[sectionId]?.listingIds;
  const allListingEntities = listingIds ? getListingEntitiesById(listingIds) : [];
  // The Figma has no carousel: a listings row is one static row of numColumns
  // cards, and depth lives behind VIEW ALL. Extra fetched listings are simply
  // not shown. On phones the row still swipes ÔÇö a single column of four cards
  // would not fit the reserved height, and swiping is the native pattern there.
  const listingEntities = allListingEntities.slice(0, numColumns);

  const fetched = featuredListingData?.[sectionId]?.fetched || false;
  const inProgress = featuredListingData?.[sectionId]?.inProgress;

  const error = featuredListingData?.[sectionId]?.error;

  const numberOfListings = listingEntities?.length > 0 ? listingIds?.length : 0;

  const [carouselWidthConstant, setCarouselWidthConstant] = useState(null);
  const [mounted, setMounted] = useState(false);

  const containerRef = React.useRef(null);
  const sliderRef = React.useRef(null);

  // force mobile styles if we render this section within a modal
  const isMobile = mounted && isMobileViewport();
  const isMobileBreakpoint = isMobile || isInsideContainer;

  useEffect(() => {
    setMounted(true);
  }, []);

  // Fetch listing data as soon as SectionListings mounts ÔÇö don't wait for the
  // lazy-loaded carousel to enter the viewport. The carousel height calculation
  // can produce 0px before the ResizeObserver fires, which prevents the
  // lazyLoadWithDimensions HOC from ever rendering, meaning the fetch inside
  // ListingCarouselComponent would never fire.
  useEffect(() => {
    if (!fetched && inProgress !== true && !error) {
      const listingImageConfig = config.layout.listingImage;
      onFetchFeaturedListings(sectionId, parentPage, listingImageConfig, allSections);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    // we use resizeObserver to accomodate for an edge case when this section is rendered within a modal
    if (containerRef.current) {
      const resizeObserver = new ResizeObserver(entries => {
        const carouselWidth = entries[0].contentRect.width;
        containerRef.current.style.setProperty('--carouselWidth', `${carouselWidth}px`);
        setCarouselWidthConstant(carouselWidth);
      });
      resizeObserver.observe(containerRef.current);

      return () => {
        resizeObserver.disconnect();
      };
    }
  }, []);

  const onSlideLeft = e => {
    const slider = sliderRef.current;
    if (!slider) return;
    const sliderChildElement = sliderRef.current.firstElementChild?.querySelector('a');
    const slideWidth = numColumns * sliderChildElement?.clientWidth;
    slider.scrollLeft = slider.scrollLeft - slideWidth;
    // Fix for Safari
    e.target.focus();
  };

  const onSlideRight = e => {
    const slider = sliderRef.current;
    if (!slider) return;
    const sliderChildElement = sliderRef.current.firstElementChild?.querySelector('a');
    const slideWidth = numColumns * sliderChildElement?.clientWidth;
    slider.scrollLeft = slider.scrollLeft + slideWidth;
    // Fix for Safari
    e.target.focus();
  };

  const onKeyDown = e => {
    if (e.key === KEY_ARROW_LEFT) {
      // Prevent changing cursor position in input
      e.preventDefault();
      onSlideLeft(e);
    } else if (e.key === KEY_ARROW_RIGHT) {
      // Prevent changing cursor position in input
      e.preventDefault();
      onSlideRight(e);
    }
  };

  const fieldComponents = options?.fieldComponents;
  const fieldOptions = { fieldComponents };
  const hasHeaderFields = hasDataInFields([title, description, callToAction], fieldOptions);
  const darkMode = appearance?.textColor === 'white';
  const noListingsFound = fetched && listingEntities.length === 0;

  // The curated featured row is the one that queries the featured flag ÔÇö
  // per the Figma it carries the filter pills. Detected from its own query so
  // renaming the section in Console cannot silently drop the pills.
  // isFeatured lives in publicData, so the filter key is pub_isFeatured.
  const isFeaturedRow = /pub_isFeatured|meta_featured/.test(listingSearchQuery || '');

  // The Figma puts a quiet VIEW ALL on every listings row. Rendered by default
  // so it cannot be forgotten in Console; an operator-set callToAction wins.
  const hasOwnCta = callToAction?.fieldType && callToAction.fieldType !== 'none';

  const carouselHeight = calculateCarouselHeight(
    numColumns,
    config,
    carouselWidthConstant,
    isMobileBreakpoint,
    error,
    noListingsFound
  );

  return (
    <SectionContainer
      id={sectionId}
      className={className}
      rootClassName={rootClassName}
      appearance={appearance}
    >
      {hasHeaderFields ? (
        <header className={defaultClasses.sectionDetails}>
          <Field data={title} className={defaultClasses.title} options={fieldOptions} />
          <Field data={description} className={defaultClasses.description} options={fieldOptions} />
          <Field data={callToAction} className={defaultClasses.ctaButton} options={fieldOptions} />
          {!hasOwnCta ? (
            <NamedLink name="SearchPage" className={css.viewAllLink}>
              <FormattedMessage id="SectionListings.viewAll" />
            </NamedLink>
          ) : null}
        </header>
      ) : null}

      {isFeaturedRow ? <FilterPills /> : null}

      <div className={css.carouselContainer}>
        {/* No carousel arrows: the row is static per the Figma ÔÇö see slice above. */}
        <div className={css.dynamicContainer} style={{ height: carouselHeight }} ref={containerRef}>
          {/* Lazy-loaded carousel component renders when in viewport. We don't use lazy loading if component is rendered within a modal */}
          {isInsideContainer ? (
            <ListingCarouselComponent
              numColumns={numColumns}
              listings={listingEntities}
              sliderRef={sliderRef}
              darkMode={darkMode}
              onFetchFeaturedListings={onFetchFeaturedListings}
              fetched={fetched}
              inProgress={inProgress}
              parentPage={parentPage}
              sectionId={sectionId}
              error={error}
              config={config}
              allSections={allSections}
              isInsideContainer={isInsideContainer}
            />
          ) : (
            <LazyListingCarouselComponent
              numColumns={numColumns}
              listings={listingEntities}
              sliderRef={sliderRef}
              darkMode={darkMode}
              onFetchFeaturedListings={onFetchFeaturedListings}
              fetched={fetched}
              inProgress={inProgress}
              parentPage={parentPage}
              sectionId={sectionId}
              error={error}
              config={config}
              allSections={allSections}
              isInsideContainer={isInsideContainer}
            />
          )}
        </div>
      </div>
    </SectionContainer>
  );
};
export default SectionListings;
